import { formatWeekdayShort, parseCalendarDate } from '../../lib/formatDate';

/**
 * The calendar's grid arithmetic, kept out of the component so the geometry can be reasoned
 * about on its own — the same split `components/tree/layout.ts` takes.
 *
 * **Everything here works in calendar-date space**: a `YYYY-MM-DD` string in, a
 * `YYYY-MM-DD` string out, and every intermediate `Date` built from local y/m/d parts
 * through `parseCalendarDate`. Nothing is ever parsed as an instant or converted to a zone.
 * A day on a calendar is not a moment — put one through `new Date(iso)` and a reader west of
 * UTC gets a grid whose cells are all one day out, which is the same defect
 * `formatDateOnly` exists to prevent one layer up.
 */

/** One cell of the grid. */
export interface CalendarCell {
  /** `YYYY-MM-DD`. */
  date: string;
  /** 1..31 — the number the cell renders, zero-padded at the call site. */
  day: number;
  /**
   * False for the leading and trailing days borrowed from the neighbouring months. They are
   * real, selectable dates; they are just not part of the month being shown, which is what
   * the muted tone says.
   */
  inMonth: boolean;
}

/**
 * **The week starts on Monday.** Product decision, and it is a decision rather than a
 * default: it puts Saturday and Sunday together at the end of the row, so a reader looking
 * for a working day never has a weekend splitting their scan in two.
 *
 * It is deliberately NOT read from the viewer's locale. The grid, the weekday header and
 * every offset below are one fact, and a locale-dependent week start would make the layout
 * differ between two people looking at the same roster.
 */
const WEEK_START = 1;

const DAYS_IN_WEEK = 7;

/**
 * The seven column headings, in the grid's own order — `MON TUE WED THU FRI SAT SUN`.
 *
 * The names come from `formatWeekdayShort`, so they follow the viewer's locale and no date
 * is turned into a string outside `lib/formatDate.ts`. The **order** is this file's, because
 * it has to match `WEEK_START` above — the headings and the offsets are one fact, and two
 * places deciding where the week begins is a header that names the wrong columns.
 *
 * Built from a known week — 04 Jan 2016 was a Monday — because a week has to start somewhere
 * and "today" would rotate the labels daily.
 */
export const WEEKDAY_LABELS: readonly string[] = Array.from({ length: DAYS_IN_WEEK }, (_, index) =>
  formatWeekdayShort(new Date(2016, 0, 4 + (index + WEEK_START - 1), 12)),
);

/** A local `Date` back to `YYYY-MM-DD`, reading the local parts rather than the instant. */
export function toCalendarDate(date: Date): string {
  const month = `${date.getMonth() + 1}`.padStart(2, '0');
  const day = `${date.getDate()}`.padStart(2, '0');

  return `${date.getFullYear()}-${month}-${day}`;
}

/** Today, in the viewer's own zone — the only place the calendar asks what day it is. */
export function todayCalendarDate(): string {
  return toCalendarDate(new Date());
}

/** The `YYYY-MM` a date falls in. */
export function monthOf(date: string): string {
  return date.slice(0, 7);
}

/** The month `delta` months away. Rolls the year over, and back. */
export function addMonths(month: string, delta: number): string {
  const start = parseCalendarDate(`${month}-01`);

  if (!start) {
    return month;
  }

  // Day 1 throughout, so a 31-day month stepping into a 30-day one cannot overflow into the
  // month after it — `new Date(2016, 0, 31)` plus one month is 02 March, not February.
  return monthOf(toCalendarDate(new Date(start.getFullYear(), start.getMonth() + delta, 1, 12)));
}

/**
 * The same day of the month `delta` months away, **clamped to the length of the month it
 * lands in** — 31 Jan back one month is 29 Feb in 2016 and 28 Feb in 2017, never the 31st of
 * a February that has no 31st.
 *
 * The clamp is the whole reason this exists rather than splicing a month onto a day string.
 * `2016-02-31` is not a date: `Date` normalises it forward to 02 March, so paging up from
 * 31 January would have skipped February altogether and landed the reader a month past where
 * they were going.
 */
export function addMonthsToDate(date: string, delta: number): string {
  const start = parseCalendarDate(date);

  if (!start) {
    return date;
  }

  const target = new Date(start.getFullYear(), start.getMonth() + delta, 1, 12);
  const daysInTarget = new Date(target.getFullYear(), target.getMonth() + 1, 0, 12).getDate();

  return toCalendarDate(
    new Date(target.getFullYear(), target.getMonth(), Math.min(start.getDate(), daysInTarget), 12),
  );
}

/** The date `delta` days away. Rolls across month and year boundaries. */
export function addDays(date: string, delta: number): string {
  const start = parseCalendarDate(date);

  if (!start) {
    return date;
  }

  return toCalendarDate(new Date(start.getFullYear(), start.getMonth(), start.getDate() + delta, 12));
}

/**
 * The cells for one month, in reading order, Monday first.
 *
 * **The grid is as many whole weeks as the month needs — five or six, never a fixed six.**
 * January 2016 starts on a Friday and ends on a Sunday, so it is exactly five rows with four
 * borrowed days at the front and none at the back. Padding it to six would leave a row of
 * greyed-out April dates under a March that did not need them, and the panel would change
 * height from month to month for no reason the reader can see.
 */
export function buildMonthGrid(month: string): CalendarCell[] {
  const first = parseCalendarDate(`${month}-01`);

  if (!first) {
    return [];
  }

  const year = first.getFullYear();
  const monthIndex = first.getMonth();

  // getDay() is Sunday-based (0..6). Rotating by the week start maps Monday to 0 and Sunday
  // to 6, which is how many days have to be borrowed from the month before.
  const leading = (first.getDay() - WEEK_START + DAYS_IN_WEEK) % DAYS_IN_WEEK;

  // Day 0 of the next month is the last day of this one.
  const daysInMonth = new Date(year, monthIndex + 1, 0, 12).getDate();
  const weeks = Math.ceil((leading + daysInMonth) / DAYS_IN_WEEK);

  return Array.from({ length: weeks * DAYS_IN_WEEK }, (_, index) => {
    // Offsets outside the month are left to Date's own normalisation, which is what makes a
    // borrowed day carry its real date rather than a number with no month behind it.
    const cell = new Date(year, monthIndex, index - leading + 1, 12);

    return {
      date: toCalendarDate(cell),
      day: cell.getDate(),
      inMonth: cell.getMonth() === monthIndex && cell.getFullYear() === year,
    };
  });
}
